// mission-lock-classify-existing.ts
//
// Tách THUẦN từ mission-lock.ts (phase 10, lô A): phân loại lock đã tồn tại khi
// `acquireMachineLock` gặp EEXIST — không bao giờ tự xoá, chỉ ném lỗi phù hợp
// (§9.4). Đọc lại có retry vì `fs.open(wx)` báo EEXIST có thể trúng khoảnh khắc
// writer khác chưa flush xong nội dung (xem comment gốc dưới).

import { MachineLockBusyError, MachineLockRecoveringError } from "./mission-lock-errors.js";
import { readMachineLock } from "./mission-lock-file-io.js";
import type { LockLivenessProbe } from "./process-liveness-probe.js";

// Vừa thấy `fs.open(wx)` báo EEXIST (file đã tồn tại) thì đọc lại NGAY có thể trúng
// khoảnh khắc writer khác đã tạo file (0 byte) nhưng chưa flush xong nội dung
// (open → writeFile → sync → close không nguyên tử với nhau) ⇒ JSON.parse rỗng
// ném SyntaxError, readMachineLock nuốt lỗi trả undefined ⇒ hiểu nhầm "không đọc
// được" thành lock hỏng thật, trong khi chỉ là chưa ghi xong (race, đo thực nghiệm
// trên macOS: ~5-10% lần race khi 2 acquire cùng lúc). Thử lại vài lần, cách nhau
// vài ms, trước khi kết luận "không đọc được" — vẫn giữ nguyên bán chất "unknown ⇒
// RECOVERING" cho trường hợp THẬT SỰ hỏng/không có sau khi đã thử lại.
export const EXISTING_LOCK_READ_RETRIES = 5;
export const EXISTING_LOCK_READ_RETRY_DELAY_MS = 10;

async function readMachineLockWithRetry(lockPath: string) {
  for (let attempt = 0; attempt <= EXISTING_LOCK_READ_RETRIES; attempt++) {
    const existing = await readMachineLock(lockPath);
    if (existing !== undefined) return existing;
    if (attempt === EXISTING_LOCK_READ_RETRIES) return undefined;
    await new Promise((resolve) => setTimeout(resolve, EXISTING_LOCK_READ_RETRY_DELAY_MS));
  }
  return undefined;
}

export async function classifyExistingLock(
  lockPath: string,
  liveness: LockLivenessProbe,
): Promise<never> {
  const existing = await readMachineLockWithRetry(lockPath);
  if (existing === undefined) {
    // File có nhưng không đọc/parse được (kể cả sau khi thử lại) ⇒ không biết ai
    // giữ ⇒ RECOVERING.
    throw new MachineLockRecoveringError(
      `Machine lock ${lockPath} tồn tại nhưng không đọc được. Không thể xác định chủ ⇒ ` +
        "RECOVERING. Chạy `avatar autopilot unlock` để xem bước gỡ tay an toàn.",
    );
  }

  if (await liveness.isAlive(existing.pid)) {
    // Còn sống — nhưng có đúng là CHỦ CŨ không, hay PID đã bị tái sử dụng?
    const currentStart = await liveness.processStartedAt(existing.pid);
    if (
      existing.processStartedAt !== undefined &&
      currentStart !== undefined &&
      currentStart !== existing.processStartedAt
    ) {
      throw new MachineLockRecoveringError(
        `PID ${existing.pid} trong lock ${lockPath} đã bị TÁI SỬ DỤNG (start-time ghi trong lock ` +
          `"${existing.processStartedAt}" ≠ start-time hiện tại "${currentStart}"). ` +
          "Không thể kết luận mission cũ đã chết ⇒ RECOVERING, KHÔNG giành lock (§9.4) — chạy `avatar autopilot unlock` để gỡ.",
      );
    }
    throw new MachineLockBusyError(
      `Đã có mission "${existing.missionId}" đang chạy trên máy này (pid ${existing.pid}, ` +
        `lock ${lockPath}). Mỗi máy chỉ chạy MỘT mission.`,
    );
  }

  // Chủ đã chết — nhưng con của nó có thể còn sống (§15: đừng đẻ thêm con).
  for (const childPid of existing.childPids ?? []) {
    if (await liveness.isAlive(childPid)) {
      throw new MachineLockRecoveringError(
        `Supervisor (pid ${existing.pid}) của mission "${existing.missionId}" (lock ${lockPath}) đã chết nhưng ` +
          `tiến trình con pid ${childPid} CÒN SỐNG. Không spawn mission mới ⇒ RECOVERING: ` +
          `dừng pid ${childPid} rồi chạy \`avatar autopilot unlock\`.`,
      );
    }
  }

  // R-25 (phase 09, N3): mission-lease-heartbeat.ts (đọc "im lặng Xs" từ
  // heartbeatAt) bị xoá — heartbeat "không bao giờ tự xoá lock" (chính header
  // module cũ) nên là tín hiệu không ai đọc. Thông báo giờ mô tả lock bằng
  // thời điểm TẠO (acquiredAt), không còn nhắc khoảng im lặng.
  throw new MachineLockRecoveringError(
    `Mission "${existing.missionId}" để lại lock mồ côi ${lockPath} (lock tạo lúc ` +
      `${existing.acquiredAt}, pid ${existing.pid} đã chết). Lock KHÔNG tự thu hồi — ` +
      "chạy `avatar autopilot unlock` để gỡ (§9.4: không bao giờ tự steal).",
  );
}
